import { Breadcrumb, Button, Form, Input, InputNumber } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import { API } from '@/api'
import { ServerGatewayInfo } from '@/api/type-server.ts'
import LeftArrow from '@/assets/icons/left-arrow.svg?react'
import { Divider, message, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown.tsx'
import { GhostType, MachineType, PaymentType } from '@/consts/machine.ts'
import { Deployment } from '@/models/deployment.ts'
import { Image } from '@/models/image.ts'
import { Machine } from '@/models/machine.ts'
import { Snapshot } from '@/models/snapshot.ts'
import { MachineSelector } from '@/pages/create-instance/components/machine-selector.tsx'

type FormValuesType = {
  name: string
  description: string
  machine: Machine
  fromImageId: number
  fromSnapId: number
  port: number
  cmd: string
}

export function DeploymentCreateSubPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const editingDeployment = location.state?.editingDeployment as Deployment
  const isEditingDeployEnabled = location.state?.isEditingDeployEnabled as boolean
  const [form] = Form.useForm<FormValuesType>()
  const [loading, setLoading] = useState(false)

  const [type, setType] = useState(MachineType.GPU)
  const period = PaymentType.Hourly
  const [count] = useState(1)
  const [checkedImage, setCheckedImage] = useState<Image>()
  const [checkedSnap, setCheckedSnap] = useState<Snapshot>()
  const [isRequiredMachine, setIsRequiredMachine] = useState(false)
  const [gatewayList, setGatewayList] = useState<ServerGatewayInfo[]>([])
  const [machines, setMachines] = useState<Machine[]>([])
  const [images, setImages] = useState<Image[]>([])
  const [snapshots, setSnapshots] = useState<Snapshot[]>([])
  const [machine, setMachine] = useState<Machine>()
  const [selectedGhostType, setSelectedGhostType] = useState<GhostType>(GhostType.Image)
  const [isInitialSetupDone, setIsInitialSetupDone] = useState(false)

  const { t } = useTranslation()

  const handleSetCheckedImage = (image?: Image) => {
    setCheckedImage(image)
    form.setFieldsValue({ machine: machine })
    form.setFieldsValue({ fromImageId: image?.id })
    form.setFieldsValue({ fromSnapId: 0 })
  }

  const handleSetCheckedSnap = (snap?: Snapshot) => {
    setCheckedSnap(snap)
    form.setFieldsValue({ machine: machine })
    form.setFieldsValue({ fromImageId: 0 })
    form.setFieldsValue({ fromSnapId: snap?.id })
  }

  const fetchGatewayList = useCallback(() => {
    if (!machine?.domains?.length) return
    setLoading(true)

    API.getGatewayList({
      domain: machine?.domains[0] ?? 0,
      public: true,
      page: 1,
      per_page: 5,
    })
      .then((res) => {
        if (res.code === 0) {
          setGatewayList(res.gateways)
        }
      })
      .finally(() => setLoading(false))
  }, [machine?.domains, setGatewayList])

  useEffect(() => {
    fetchGatewayList()
  }, [fetchGatewayList])

  // Effect for setting initial state and basic form values when editing
  useEffect(() => {
    if (editingDeployment) {
      // Set machine type
      const machineClassTag = editingDeployment.runtimeEnv.hardwareEnv.resource.machineClassTag
      if (machineClassTag.includes('cpu')) {
        setType(MachineType.CPU)
      } else {
        setType(MachineType.GPU)
      }

      // Set ghost type
      const { fromImageId } = editingDeployment.runtimeEnv.systemEnv
      if (fromImageId !== 0) {
        setSelectedGhostType(GhostType.Image)
      } else {
        setSelectedGhostType(GhostType.Snapshot)
      }

      // Set basic form values immediately
      form.setFieldsValue({
        name: editingDeployment.name,
        description: editingDeployment.description,
        port: editingDeployment.gateway.port,
        cmd: editingDeployment.runtimeEnv.systemEnv.cmd,
      })
    }
  }, [editingDeployment, form])

  // Effect for setting machine, image, and snapshot once their lists are available
  useEffect(() => {
    if (!editingDeployment || isInitialSetupDone) return

    // Set machine once machines list is loaded
    if (machines && machines.length > 0) {
      const machine = machines.find(
        (m) =>
          m.unitSpec.classTag === editingDeployment.runtimeEnv.hardwareEnv.resource.machineClassTag,
      )
      if (machine) setMachine(machine)
    }

    const { fromImageId, fromSnapshotId } = editingDeployment.runtimeEnv.systemEnv

    // Set image once images list is loaded
    if (fromImageId !== 0 && images && images.length > 0) {
      const image = images.find((i) => i.id === fromImageId)
      if (image) handleSetCheckedImage(image)
    }

    // Set snapshot once snapshots list is loaded
    if (fromSnapshotId !== 0 && snapshots && snapshots.length > 0) {
      const snap = snapshots.find((i) => i.id === fromSnapshotId)
      if (snap) handleSetCheckedSnap(snap)
    }

    if (machine && (checkedImage || checkedSnap)) {
      setIsInitialSetupDone(true)
    }
  }, [
    editingDeployment,
    machines,
    images,
    snapshots,
    form,
    isInitialSetupDone,
    machine,
    checkedImage,
    checkedSnap,
  ])

  function handleSubmit(fieldsValue: FormValuesType) {
    setLoading(true)
    if (gatewayList.length === 0) {
      message.error('No available gateway yet, please choose another machine')
      setLoading(false)
      return
    }

    const gtwResId = gatewayList?.[0]?.resID || editingDeployment.gateway.resID

    let fromImageId =
      fieldsValue?.fromImageId === 0 ? 0 : fieldsValue?.fromImageId || checkedImage?.id
    let fromSnapshotId =
      fieldsValue?.fromSnapId === 0 ? 0 : fieldsValue?.fromSnapId || checkedSnap?.id || 0

    if (fromImageId !== 0) {
      fromSnapshotId = 0
    } else if (fromSnapshotId !== 0) {
      fromImageId = 0
    }

    if (fromImageId === 0 && fromSnapshotId === 0) {
      message.error('Please select an image or snapshot')
      setLoading(false)
      return
    }

    if (editingDeployment) {
      API.updateDeployment({
        res_id: editingDeployment.resID,
        port: fieldsValue.port,
        domain: machine?.domains?.[0] || 0,
        system_env: JSON.stringify({
          fromImageId: fromImageId,
          fromSnapshotId: fromSnapshotId,
          cmd: fieldsValue.cmd || '',
          envs: '',
          extraPorts: [fieldsValue.port],
        }),
        name: fieldsValue.name,
        description: fieldsValue.description,
        gtw_res_id: gtwResId,
        hardware_resource: JSON.stringify({
          cpus: (machine?.unitSpec.cpuCores ?? 0) * count,
          gpus: (machine?.unitSpec.gpuCores ?? 0) * count,
          diskGB: (machine?.unitSpec.diskGB ?? 0) * count,
          gpuName: machine?.unitSpec.gpuName,
          domain: '',
          agentDomains: [machine?.domains?.[0] ?? 0],
          agentDriver: 1,
          cpuName: machine?.unitSpec.cpuName,
          ramMB: (machine?.unitSpec.ramMB ?? 0) * count,
          gpuRamMB: (machine?.unitSpec.gpuRamMB ?? 0) * count,
          attributeTags: 64,
          machineClassTag: `${machine?.unitSpec.classTag}`,
          machineCategory: 0,
          unitQty: count,
        }),
        enabled: isEditingDeployEnabled,
      }).then((res) => {
        if (res.code === 0) {
          setLoading(false)
          navigate('/deploy')
        }
      })
    } else {
      API.createDeployment({
        port: fieldsValue.port,
        domain: machine?.domains?.[0] || 0,
        system_env: JSON.stringify({
          fromImageId: fromImageId,
          fromSnapshotId: fromSnapshotId,
          cmd: fieldsValue.cmd || '',
          envs: '',
          extraPorts: [fieldsValue.port],
        }),
        name: fieldsValue.name,
        description: fieldsValue.description,
        gtw_res_id: gtwResId,
        hardware_resource: JSON.stringify({
          cpus: (machine?.unitSpec.cpuCores ?? 0) * count,
          gpus: (machine?.unitSpec.gpuCores ?? 0) * count,
          diskGB: (machine?.unitSpec.diskGB ?? 0) * count,
          gpuName: machine?.unitSpec.gpuName,
          domain: '',
          agentDomains: [machine?.domains?.[0] ?? 0],
          agentDriver: 1,
          cpuName: machine?.unitSpec.cpuName,
          ramMB: (machine?.unitSpec.ramMB ?? 0) * count,
          gpuRamMB: (machine?.unitSpec.gpuRamMB ?? 0) * count,
          attributeTags: 64,
          machineClassTag: `${machine?.unitSpec.classTag}`,
          machineCategory: 0,
          unitQty: count,
        }),
      }).then((res) => {
        if (res.code === 0) {
          setLoading(false)
          navigate('/deploy')
        }
      })
    }
  }

  return (
    <>
      <div className="card bg-white">
        <div className="flex items-center space-x-4 mb-4">
          <Link to="/deploy">
            <div className="w-8 h-8 icon-btn">
              <LeftArrow />
            </div>
          </Link>
          <Breadcrumb
            items={[
              {
                title: (
                  <Link to="/deploy" className="h6 text-secondary !hover:text-red-600">
                    <Trans i18nKey="deploy.title">Auto Deploy</Trans>
                  </Link>
                ),
              },
              {
                title: (
                  <span className="h6 text-primary">
                    {editingDeployment ? (
                      <Trans i18nKey="deploy.createSubPage.editTitle">Edit Deploy</Trans>
                    ) : (
                      <Trans i18nKey="deploy.createSubPage.title">New Deploy</Trans>
                    )}
                  </span>
                ),
              },
            ]}
            separator={<span className="h6 text-primary">/</span>}
          />
        </div>

        <Divider />

        <Form
          form={form}
          validateTrigger={['onChange', 'onBlur']}
          layout="vertical"
          name="create-scaling-spec-form"
          scrollToFirstError={true}
        >
          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="deploy.createSubPage.form.name">Deploy Name</Trans>
              </div>
            }
            id="name"
            name="name"
            className="mb-8"
            rules={[{ required: true }]}
          >
            <Input className="h-11 px-4 py-3 t-sb" placeholder="Please Enter Deploy Name" />
          </Form.Item>
          <Form.Item
            label={
              <div className="t-sb">
                <Trans i18nKey="deploy.createSubPage.form.description">Deploy Description</Trans>
              </div>
            }
            id="description"
            name="description"
            className="mb-8"
          >
            <Input.TextArea
              className="h-[88px] px-4 py-3 t-sb"
              placeholder={t('deploy.createSubPage.form.descriptionPlaceholder')}
            />
          </Form.Item>

          <div className="mb-8 py-2">
            <span className="h6 pl-2 border-l-4 border-[#2bcaa5]">
              <Trans i18nKey="deploy.createSubPage.form.deployTargetTitle">Deploy Target</Trans>
            </span>
          </div>

          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="deploy.createSubPage.form.accessMethod">Access Method</Trans>
              </div>
            }
            id="accessMethod"
            name="accessMethod"
            className="mb-8 t-sb"
          >
            <Dropdown
              options={[
                {
                  label: t('deploy.createSubPage.form.accessMethodPublic'),
                  value: 'public',
                  className: 't-sb',
                },
                {
                  label: t('deploy.createSubPage.form.accessMethodPrivate'),
                  value: 'private',
                  className: 't-sb',
                  disabled: true,
                },
              ]}
            />
          </Form.Item>

          <div id="fromImageId" className="mb-4 t-sb text-with-star">
            <Trans i18nKey="deploy.createSubPage.form.instanceImage">Instance & Image</Trans>
          </div>
          {isRequiredMachine && !checkedImage && !checkedSnap && (
            <div className="mb-4 text-red-600">
              <Trans i18nKey="deploy.createSubPage.form.instanceImage">
                Please Select a instance and image
              </Trans>
            </div>
          )}

          <Form.Item name="machine" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="fromImageId" hidden>
            <Input />
          </Form.Item>

          <Form.Item name="fromSnapId" hidden>
            <Input />
          </Form.Item>

          <MachineSelector
            type={type}
            period={period}
            onTypeChange={setType}
            onChange={setMachine}
            count={count}
            checkedImage={checkedImage}
            setCheckedImage={(value) => handleSetCheckedImage(value)}
            checkedSnap={checkedSnap}
            setCheckedSnap={(value) => handleSetCheckedSnap(value)}
            onMachinesChange={setMachines}
            onImagesChange={setImages}
            onSnapshotsChange={setSnapshots}
            selectedMachine={machine}
            selectedGhostType={selectedGhostType}
            workloadTypes={['all']}
          />

          <Form.Item
            label={
              <div className="t-sb text-with-star">
                <Trans i18nKey="deploy.createSubPage.form.port">Port (HTTP/HTTPS)</Trans>
              </div>
            }
            name="port"
            rules={[{ required: true }]}
          >
            <InputNumber
              className="h-11 t-sb w-full py-2"
              placeholder="Please Enter Port"
              min={0}
              max={65535}
            />
          </Form.Item>

          <Form.Item
            label={
              <div className="t-sb">
                <Trans i18nKey="deploy.createSubPage.form.cmd">Start Command</Trans>
              </div>
            }
            name="cmd"
          >
            <Input
              className="h-11 px-4 py-3 t-sb"
              placeholder={t('deploy.createSubPage.form.cmdPlaceholder')}
            />
          </Form.Item>

          <Form.Item
            label={
              <div className="t-sb">
                <Trans i18nKey="deploy.createSubPage.form.maxNumberIns">
                  Maximum Number of Instances (Technical Preview)
                </Trans>
              </div>
            }
            name="maxInstanceCount"
            initialValue={1}
          >
            <Input
              className="h-11 px-4 py-3 t-sb disabled:bg-transparent disabled:text-primary"
              placeholder={t('deploy.createSubPage.form.maxNumberInsPlaceholder')}
              disabled
            />
          </Form.Item>

          <Form.Item>
            <div className="pt-4 flex items-end justify-end gap-4">
              <Button
                size="large"
                className="h-11 px-4 py-3 t-sb"
                onClick={() => {
                  navigate('/deploy')
                }}
              >
                <Trans i18nKey="deploy.createSubPage.form.cancelBtn">Cancel</Trans>
              </Button>
              <Button
                type="primary"
                className="h-11 px-4 py-3 t-sb"
                size="large"
                loading={loading}
                htmlType={'submit'}
                onClick={() => {
                  form.validateFields().then(() => {
                    if (checkedImage || checkedSnap) {
                      handleSubmit(form.getFieldsValue())
                    } else {
                      setIsRequiredMachine(true)
                      document.getElementById('fromImageId')?.scrollIntoView({ behavior: 'smooth' })
                      return
                    }
                  })
                }}
              >
                <Trans i18nKey="deploy.createSubPage.form.confirmBtn">Confirm</Trans>
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </>
  )
}
